/*
 * Copyright (c) Kalaora 2022.
 * Author: fharold
 *
 * hilo-oauth-manager
 * FormLabel.tsx
 */

import HiloReactComponent from "../layout/HiloReactComponent";
import React from "react";
import {AbstractForm} from "../../model/form/AbstractForm";
import {FormElement} from "./FormElement";
import FormError from "./FormError";
import HttpError from "../../utils/HttpError";
import {Field} from "../../model/form/Field";
import Button from "../element/button/Button";
import Colors from "../../model/enumerations/Colors";
import {FormException} from "../../model/form/FormException";
import {AbstractDTO} from "../../model/dto/AbstractDTO";
import {ButtonProps} from "../element/button/ButtonProps";
import saveButton from "../element/button/SaveButton";

interface Props<DTO extends AbstractDTO> {
    label: string;
    form: AbstractForm<DTO>;
    submitBtn?: ButtonProps;
    submit: (dto: Map<string, any> | FormData) => void;
}

interface State<DTO extends AbstractDTO> {
    invalidField?: Field<any>;
    loading: boolean;
}

export default class FormSection<DTO extends AbstractDTO> extends HiloReactComponent<Props<DTO>, State<DTO>> {

    /**
     * constructor
     * @param props
     * @param state
     */
    constructor(props: Props<DTO>, state: State<DTO>) {
        super(props, state);

        this.state = {
            invalidField: undefined,
            loading: false
        }
    }

    /**
     * _handleSubmission
     * @private
     */
    private _handleSubmission = async () => {
        const {form, submit} = this.props;

        await this.setState({loading: true});
        try {
            await submit(form.compile());
        } catch (e) {
            await this._setError((e instanceof FormException) ? e.field : e.toString());
        } finally {
            await this.setState({loading: false});
        }
    };

    /**
     * _setError
     * @private
     */
    private _setError = (invalidField?: Field<any>) => {
        this.setState({
            invalidField: invalidField
        });
    }

    /**
     * render
     * @returns {any}
     */
    public render() {
        const {label, form} = this.props;
        const {invalidField, loading} = this.state;

        let submitBtn = this.props.submitBtn || saveButton()

        return <div className="form-section field is-small">
            {label && <label className="label">{label}</label>}

            {[...form.fields.values()].map(field => {
                return <div key={`${field.name}-div`}>
                    <FormElement key={field.name} field={field} onChange={form.applyValue}/>

                    {invalidField?.name === field.name &&
                    <FormError key={`${field.name}-error`} field={invalidField}/>}
                </div>
            })}

            {
                this.props.children
            }

            {form.statusCode && <HttpError statusCode={form.statusCode}/>}

            {submitBtn && <Button color={submitBtn?.color || Colors.blue} onClick={this._handleSubmission}
                    labelAccessor={submitBtn?.labelAccessor || (t => t.save)} loading={submitBtn?.loading || loading}
                    disabled={submitBtn?.disabled || loading}/>}
        </div>;
    }
}